import React from "react";
import { useNavigate } from "react-router";
import useAccess from "../hooks/useAccess.jsx";
import useDispatchCaptureUnreadIndicators from "../hooks/useDispatchCaptureUnreadIndicators.js";
import { ACCESS_PERMISSION_KEYS, hasPermission } from "../lib/accessControl.js";
import {
  DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME,
  DISPATCH_CAPTURE_UNREAD_CLASS_NAME,
} from "../lib/dispatchCaptureArrivalHighlight.js";
import {
  buttonSizeClasses,
  joinClasses,
} from "./resultsPrimitives/shared.js";

export const DISPATCH_ALERT_CAPTURES_PATH = "/system-config/dispatch-alert-captures";
export const REASSIGNMENT_DIAGNOSTICS_PATH = "/system-config/reassignment-diagnostics";
export const SOLLIO_UNIAG_ORDER_IMPORT_HISTORY_PATH = "/captures?view=imports";
export const REPORT_ACTION_CAPTURE_HISTORY_PATH = "/captures?view=reports";
export const COMBINED_CAPTURE_ACTIVITY_HISTORY_PATH = "/captures";

const SWITCHER_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
]);
const SWITCHER_ACTIVE_CLASS_NAME = "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong";
const SWITCHER_INACTIVE_CLASS_NAME = "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";

export default function DispatchCaptureHistorySwitcher({
  active = "all",
  onSelect,
}) {
  const navigate = useNavigate();
  const { access } = useAccess();
  const canSeeRobot = hasPermission(access, ACCESS_PERMISSION_KEYS.assistantQuestionsManage);
  const isRobotActive = active === "robot";
  const isAllActive = active === "all";
  const isErrorsActive = active === "errors";
  const isImportsActive = active === "imports";
  const isReportsActive = active === "reports";
  const isJournalActive = active === "journal";
  const isSourceChangesActive = active === "source_changes";
  const isConsolidationActive = active === "consolidation";
  const isReassignmentActive = active === "reassignment";
  const {
    arrivingJournal,
    unreadJournal,
    arrivingReports,
    unreadReports,
    arrivingSourceChanges,
    unreadSourceChanges,
    arrivingConsolidation,
    unreadConsolidation,
    arrivingErrors,
    arrivingReassignment,
    unreadErrors,
    unreadReassignment,
  } = useDispatchCaptureUnreadIndicators(active);
  const allArriving = isAllActive && (arrivingReassignment || arrivingSourceChanges || arrivingConsolidation || arrivingReports || arrivingJournal);
  const allBlinkClassName = allArriving
    ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
    : "";
  const errorsBlinkClassName = !isErrorsActive && unreadErrors
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isErrorsActive && arrivingErrors
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";
  const reassignmentBlinkClassName = !isReassignmentActive && unreadReassignment
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isReassignmentActive && arrivingReassignment
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";
  const sourceChangesBlinkClassName = !isSourceChangesActive && unreadSourceChanges
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isSourceChangesActive && arrivingSourceChanges
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";
  const consolidationBlinkClassName = !isConsolidationActive && unreadConsolidation
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isConsolidationActive && arrivingConsolidation
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";
  const reportsBlinkClassName = !isReportsActive && unreadReports
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isReportsActive && arrivingReports
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";
  const journalBlinkClassName = !isJournalActive && unreadJournal
    ? DISPATCH_CAPTURE_UNREAD_CLASS_NAME
    : isJournalActive && arrivingJournal
      ? DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME
      : "";

  const selectView = (nextView) => {
    if (typeof onSelect === "function") {
      onSelect(nextView);
      return;
    }

    if (nextView === "all") {
      navigate(COMBINED_CAPTURE_ACTIVITY_HISTORY_PATH);
      return;
    }
    if (nextView === "source_changes" || nextView === "consolidation") {
      navigate(`/captures?view=${nextView}`);
      return;
    }
    if (nextView === "journal" || nextView === "robot") {
      navigate(`/captures?view=${nextView}`);
      return;
    }
    if (nextView === "reports") {
      navigate(REPORT_ACTION_CAPTURE_HISTORY_PATH);
      return;
    }
    if (nextView === "reassignment") {
      navigate(REASSIGNMENT_DIAGNOSTICS_PATH);
      return;
    }
    navigate(nextView === "errors" ? DISPATCH_ALERT_CAPTURES_PATH : SOLLIO_UNIAG_ORDER_IMPORT_HISTORY_PATH);
  };

  return (
    <div
      className="inline-flex flex-wrap items-center gap-1.5"
      data-dispatch-capture-history-switcher="true"
      role="tablist"
      aria-label="Historiques de captures et imports"
    >
      <button
        aria-current={isAllActive ? "page" : undefined}
        aria-selected={isAllActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isAllActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          allBlinkClassName,
        ])}
        data-dispatch-capture-arrival={allArriving ? "true" : undefined}
        onClick={() => {
          if (!isAllActive) {
            selectView("all");
          }
        }}
        role="tab"
        type="button"
      >
        Toutes les activités
      </button>
      <button
        aria-current={isReassignmentActive ? "page" : undefined}
        aria-label={!isReassignmentActive && unreadReassignment ? "Dispatch, nouvelle activité" : undefined}
        aria-selected={isReassignmentActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isReassignmentActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          reassignmentBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isReassignmentActive && arrivingReassignment ? "true" : undefined}
        data-dispatch-capture-unread={!isReassignmentActive && unreadReassignment ? "true" : undefined}
        onClick={() => {
          if (!isReassignmentActive) {
            selectView("reassignment");
          }
        }}
        role="tab"
        type="button"
      >
        Dispatch
      </button>
      <button
        aria-current={isSourceChangesActive ? "page" : undefined}
        aria-label={!isSourceChangesActive && unreadSourceChanges ? "Mise à jour, nouvelle activité" : undefined}
        aria-selected={isSourceChangesActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isSourceChangesActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          sourceChangesBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isSourceChangesActive && arrivingSourceChanges ? "true" : undefined}
        data-dispatch-capture-unread={!isSourceChangesActive && unreadSourceChanges ? "true" : undefined}
        onClick={() => {
          if (!isSourceChangesActive) {
            selectView("source_changes");
          }
        }}
        role="tab"
        type="button"
      >
        Mise à jour
      </button>
      <button
        aria-current={isConsolidationActive ? "page" : undefined}
        aria-label={!isConsolidationActive && unreadConsolidation ? "Consolidation, nouvelle activité" : undefined}
        aria-selected={isConsolidationActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isConsolidationActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          consolidationBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isConsolidationActive && arrivingConsolidation ? "true" : undefined}
        data-dispatch-capture-unread={!isConsolidationActive && unreadConsolidation ? "true" : undefined}
        onClick={() => {
          if (!isConsolidationActive) {
            selectView("consolidation");
          }
        }}
        role="tab"
        type="button"
      >
        Consolidation
      </button>
      <button
        aria-current={isImportsActive ? "page" : undefined}
        aria-selected={isImportsActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isImportsActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
        ])}
        onClick={() => {
          if (!isImportsActive) {
            selectView("imports");
          }
        }}
        role="tab"
        type="button"
      >
        Imports
      </button>
      <button
        aria-current={isReportsActive ? "page" : undefined}
        aria-label={!isReportsActive && unreadReports ? "Rapports, nouveaux rapports" : undefined}
        aria-selected={isReportsActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isReportsActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          reportsBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isReportsActive && arrivingReports ? "true" : undefined}
        data-dispatch-capture-unread={!isReportsActive && unreadReports ? "true" : undefined}
        onClick={() => {
          if (!isReportsActive) {
            selectView("reports");
          }
        }}
        role="tab"
        type="button"
      >
        Rapports
      </button>
      <button
        aria-current={isJournalActive ? "page" : undefined}
        aria-label={!isJournalActive && unreadJournal ? "Journal, nouvelles confirmations" : undefined}
        aria-selected={isJournalActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isJournalActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          journalBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isJournalActive && arrivingJournal ? "true" : undefined}
        data-dispatch-capture-unread={!isJournalActive && unreadJournal ? "true" : undefined}
        onClick={() => {
          if (!isJournalActive) {
            selectView("journal");
          }
        }}
        role="tab"
        type="button"
      >
        Journal
      </button>
      {canSeeRobot ? (
        <button
          aria-current={isRobotActive ? "page" : undefined}
          aria-selected={isRobotActive}
          className={joinClasses([
            SWITCHER_BUTTON_CLASS_NAME,
            isRobotActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          ])}
          onClick={() => {
            if (!isRobotActive) {
              selectView("robot");
            }
          }}
          role="tab"
          type="button"
        >
          Robot
        </button>
      ) : null}
      <button
        aria-current={isErrorsActive ? "page" : undefined}
        aria-label={!isErrorsActive && unreadErrors ? "Erreurs, nouvelle capture" : undefined}
        aria-selected={isErrorsActive}
        className={joinClasses([
          SWITCHER_BUTTON_CLASS_NAME,
          isErrorsActive ? SWITCHER_ACTIVE_CLASS_NAME : SWITCHER_INACTIVE_CLASS_NAME,
          errorsBlinkClassName,
        ])}
        data-dispatch-capture-arrival={isErrorsActive && arrivingErrors ? "true" : undefined}
        data-dispatch-capture-unread={!isErrorsActive && unreadErrors ? "true" : undefined}
        onClick={() => {
          if (!isErrorsActive) {
            selectView("errors");
          }
        }}
        role="tab"
        type="button"
      >
        Erreurs
      </button>
    </div>
  );
}
